import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Crown, Users, Search, ChevronRight, Play, RefreshCw,
  CheckCircle, Clock, AlertCircle, FileBarChart, ArrowRight,
  Eye, ChevronDown, Globe, Zap, Target, FileText, BarChart3,
  TrendingUp, MapPin, Share2,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  generated: { label: 'Gegenereerd', color: '#94a3b8', bg: '#f1f5f9', next: 'in_review' },
  in_review: { label: 'In review', color: '#d97706', bg: '#fef3c7', next: 'approved' },
  approved: { label: 'Goedgekeurd', color: '#2563eb', bg: '#dbeafe', next: 'in_progress' },
  in_progress: { label: 'Bezig', color: '#7c3aed', bg: '#ede9fe', next: 'completed' },
  completed: { label: 'Voltooid', color: '#16a34a', bg: '#dcfce7', next: null },
};

const ICON_MAP = { Search, Target, Share2, FileText, BarChart3, TrendingUp, Globe, MapPin };

export default function ManagedClientsPage() {
  const { api } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [generating, setGenerating] = useState(null);

  const loadClients = useCallback(async () => {
    try {
      const r = await api('get', '/api/admin/managed/clients');
      setClients(r.data.clients || []);
    } catch (e) {
      toast.error('Laden mislukt');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const loadClientDetail = async (userId) => {
    setDetailLoading(true);
    setSelectedClient(userId);
    try {
      const r = await api('get', `/api/admin/managed/client/${userId}`);
      setClientDetail(r.data);
      const cats = Object.keys(r.data.service_categories || {});
      if (cats.length > 0) setActiveCategory(cats[0]);
    } catch (e) {
      toast.error('Detail laden mislukt');
    }
    setDetailLoading(false);
  };

  const generateRoadmap = async (userId) => {
    setGenerating(userId);
    try {
      const r = await api('post', `/api/admin/managed/generate-roadmap/${userId}`);
      toast.success(`Roadmap gegenereerd: ${r.data.total_tasks} taken`);
      loadClients();
      if (selectedClient === userId) loadClientDetail(userId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Genereren mislukt');
    }
    setGenerating(null);
  };

  const executeTask = async (taskId, roadmapId, userId) => {
    try {
      const r = await api('post', `/api/managed/execute-task/${taskId}`, {
        roadmap_id: roadmapId,
        user_id: userId,
      });
      toast.success('AI deliverable gegenereerd!');
      loadClientDetail(userId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI uitvoering mislukt');
    }
  };

  const updateTaskStatus = async (taskId, roadmapId, newStatus) => {
    try {
      await api('post', `/api/admin/managed/task/${taskId}/update`, {
        roadmap_id: roadmapId,
        status: newStatus,
      });
      toast.success('Taak bijgewerkt');
      if (selectedClient) loadClientDetail(selectedClient);
    } catch (e) {
      toast.error('Bijwerken mislukt');
    }
  };

  const generateReport = async (userId) => {
    try {
      await api('post', `/api/admin/managed/generate-report/${userId}`);
      toast.success('Rapport gegenereerd');
      if (selectedClient === userId) loadClientDetail(userId);
    } catch (e) {
      toast.error('Rapport genereren mislukt');
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: '32px', height: '32px', border: '3px solid #f1f5f9', borderTopColor: '#D4A017', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  // Client Detail View
  if (selectedClient && clientDetail) {
    const { user, company, roadmap, reports, service_categories } = clientDetail;
    const tasks = roadmap?.tasks || [];
    const stats = {
      total: tasks.length,
      completed: tasks.filter(t => t.status === 'completed').length,
      in_progress: tasks.filter(t => t.status === 'in_progress').length,
      in_review: tasks.filter(t => t.status === 'in_review').length,
    };

    return (
      <div data-testid="admin-managed-detail">
        <button onClick={() => { setSelectedClient(null); setClientDetail(null); }} style={{
          background: 'none', border: 'none', cursor: 'pointer', color: '#64748b',
          fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '4px',
        }}>
          ← Terug naar overzicht
        </button>

        {/* Client header */}
        <div style={{
          background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #f1f5f9',
          marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Crown size={18} style={{ color: '#D4A017' }} />
              <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
                {company?.name || user?.name}
              </h1>
            </div>
            <p style={{ fontSize: '12px', color: '#64748b' }}>{user?.email} • {company?.website}</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button data-testid="admin-gen-roadmap" onClick={() => generateRoadmap(user.id)} disabled={generating === user.id} style={{
              display: 'flex', alignItems: 'center', gap: '6px', background: '#0f172a', color: 'white',
              padding: '8px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
            }}>
              {generating === user.id ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={13} />}
              {roadmap ? 'Hergeneer roadmap' : 'Genereer roadmap'}
            </button>
            <button data-testid="admin-gen-report" onClick={() => generateReport(user.id)} style={{
              display: 'flex', alignItems: 'center', gap: '6px', background: '#f1f5f9', color: '#0f172a',
              padding: '8px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
            }}>
              <FileBarChart size={13} /> Genereer rapport
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
          {[
            { label: 'Totaal', value: stats.total, color: '#0f172a' },
            { label: 'Voltooid', value: stats.completed, color: '#16a34a' },
            { label: 'In uitvoering', value: stats.in_progress, color: '#7c3aed' },
            { label: 'In review', value: stats.in_review, color: '#d97706' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'white', borderRadius: '10px', padding: '14px', border: '1px solid #f1f5f9', textAlign: 'center' }}>
              <p style={{ fontSize: '22px', fontWeight: 800, color: s.color, fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
              <p style={{ fontSize: '10px', color: '#94a3b8' }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Task orchestration board */}
        {roadmap && (
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '12px' }}>
            {/* Category nav */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {Object.entries(service_categories).map(([catId, cat]) => {
                const Icon = ICON_MAP[cat.icon] || Search;
                const catTasks = tasks.filter(t => t.category_id === catId);
                const catDone = catTasks.filter(t => t.status === 'completed').length;
                const isActive = activeCategory === catId;
                return (
                  <button key={catId} onClick={() => setActiveCategory(catId)} style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
                    borderRadius: '8px', border: 'none', cursor: 'pointer', textAlign: 'left',
                    background: isActive ? '#0f172a' : 'white',
                  }}>
                    <Icon size={14} style={{ color: isActive ? '#D4A017' : cat.color }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '11px', fontWeight: 600, color: isActive ? 'white' : '#0f172a' }}>{cat.name}</p>
                      <p style={{ fontSize: '9px', color: isActive ? 'rgba(255,255,255,0.5)' : '#94a3b8' }}>{catDone}/{catTasks.length}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Task list with admin controls */}
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                  {service_categories[activeCategory]?.name || 'Taken'}
                </h3>
              </div>
              <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '500px', overflowY: 'auto' }}>
                {tasks.filter(t => t.category_id === activeCategory).map(task => {
                  const sc = STATUS_CONFIG[task.status] || STATUS_CONFIG.generated;
                  return (
                    <div key={task.id} data-testid={`admin-task-${task.id}`} style={{
                      padding: '12px', borderRadius: '8px', border: '1px solid #f1f5f9',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <p style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', flex: 1 }}>{task.title}</p>
                        <span style={{
                          padding: '2px 8px', borderRadius: '5px', fontSize: '9px', fontWeight: 700,
                          background: sc.bg, color: sc.color,
                        }}>{sc.label}</span>
                      </div>
                      <p style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px' }}>{task.description}</p>
                      {task.deliverable && <p style={{ fontSize: '10px', color: '#059669', marginBottom: '6px' }}>Oplevering: {task.deliverable}</p>}

                      {/* Admin action buttons */}
                      <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                        {task.status === 'generated' && (
                          <button
                            onClick={() => executeTask(task.id, roadmap.id, user.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
                              borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: 600,
                              background: '#0f172a', color: 'white',
                            }}
                          >
                            <Zap size={10} /> AI Uitvoeren
                          </button>
                        )}
                        {sc.next && (
                          <button
                            data-testid={`task-advance-${task.id}`}
                            onClick={() => updateTaskStatus(task.id, roadmap.id, sc.next)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
                              borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: 600,
                              background: STATUS_CONFIG[sc.next]?.bg || '#f1f5f9',
                              color: STATUS_CONFIG[sc.next]?.color || '#374151',
                            }}
                          >
                            → {STATUS_CONFIG[sc.next]?.label}
                          </button>
                        )}
                        {task.status !== 'completed' && (
                          <button
                            onClick={() => updateTaskStatus(task.id, roadmap.id, 'completed')}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
                              borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: 600,
                              background: '#dcfce7', color: '#16a34a',
                            }}
                          >
                            <CheckCircle size={10} /> Voltooien
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {tasks.filter(t => t.category_id === activeCategory).length === 0 && (
                  <p style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>Geen taken</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Client List View
  return (
    <div data-testid="admin-managed-overview">
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Crown size={20} style={{ color: '#D4A017' }} />
            <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>
              Managed Clients
            </h1>
          </div>
          <p style={{ color: '#64748b', fontSize: '13px' }}>Beheer IMPERIA AI Beheer klanten en hun AI-uitvoeringsroadmaps</p>
        </div>
        <div style={{ background: 'white', borderRadius: '10px', padding: '12px 18px', border: '1px solid #f1f5f9' }}>
          <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: '#D4A017' }}>{clients.length}</p>
          <p style={{ fontSize: '10px', color: '#94a3b8' }}>Premium klanten</p>
        </div>
      </div>

      {clients.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '14px', padding: '40px', border: '1px solid #f1f5f9', textAlign: 'center' }}>
          <Users size={36} style={{ color: '#94a3b8', margin: '0 auto 12px' }} />
          <p style={{ color: '#64748b', fontSize: '14px' }}>Nog geen premium klanten. Klanten die het IMPERIA AI Beheer plan afnemen verschijnen hier.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {clients.map(c => {
            const pct = c.total_tasks > 0 ? Math.round(c.completed_tasks / c.total_tasks * 100) : 0;
            return (
              <div key={c.user_id} data-testid={`managed-client-${c.user_id}`} style={{
                background: 'white', borderRadius: '12px', padding: '16px 20px', border: '1px solid #f1f5f9',
                display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer',
              }} onClick={() => loadClientDetail(c.user_id)}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                  background: 'linear-gradient(135deg, #0f172a, #1e1b4b)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Crown size={18} style={{ color: '#D4A017' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{c.company || c.name}</p>
                  <p style={{ fontSize: '11px', color: '#94a3b8' }}>{c.email} • {c.website}</p>
                </div>
                <div style={{ textAlign: 'center', minWidth: '80px' }}>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{c.completed_tasks}/{c.total_tasks}</p>
                  <p style={{ fontSize: '10px', color: '#94a3b8' }}>Taken</p>
                </div>
                <div style={{ width: '80px' }}>
                  <div style={{ height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: '#D4A017', borderRadius: '3px' }} />
                  </div>
                  <p style={{ fontSize: '10px', color: '#94a3b8', textAlign: 'right', marginTop: '2px' }}>{pct}%</p>
                </div>
                {!c.has_roadmap ? (
                  <button data-testid={`gen-roadmap-${c.user_id}`} onClick={(e) => { e.stopPropagation(); generateRoadmap(c.user_id); }} disabled={generating === c.user_id} style={{
                    display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px',
                    borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 600,
                    background: '#D4A017', color: '#0f172a',
                  }}>
                    {generating === c.user_id ? <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={12} />}
                    Genereer
                  </button>
                ) : (
                  <ChevronRight size={18} style={{ color: '#94a3b8' }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
